import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { formatCZK, formatNumCZ } from "@/lib/format";
import { TrendingUp } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

const calcMonthly = (principal, annualRate, years) => {
  const n = years * 12;
  const r = annualRate / 100 / 12;
  if (r === 0) return principal / n;
  return (principal * r) / (1 - Math.pow(1 + r, -n));
};

export const Calculator = () => {
  const { t } = useI18n();
  const [amount, setAmount] = useState(4500000);
  const [rate, setRate] = useState(4.49);
  const [years, setYears] = useState(25);

  const monthly = useMemo(
    () => calcMonthly(amount, rate, years),
    [amount, rate, years]
  );
  const total = monthly * years * 12;
  const interest = total - amount;

  return (
    <section
      id="calculator"
      data-testid="calculator-section"
      className="py-24 md:py-32 bg-muted/40 border-t border-border grain"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <span className="overline text-muted-foreground">
              {t.calc.overline}
            </span>
            <h2
              data-testid="calculator-heading"
              className="mt-5 font-heading font-semibold text-4xl md:text-5xl leading-[1.05] tracking-tight text-foreground"
            >
              {t.calc.heading_1}{" "}
              <span className="italic font-light text-primary">
                {t.calc.heading_em}
              </span>{" "}
              {t.calc.heading_2}
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-md">
              {t.calc.subtitle}
            </p>

            <div className="mt-10 hidden lg:block bg-card border border-border rounded-sm p-6">
              <div className="flex items-center gap-2 text-primary">
                <TrendingUp className="w-4 h-4" />
                <span className="overline">{t.calc.savings_label}</span>
              </div>
              <div className="mt-4 font-heading text-4xl font-semibold text-foreground">
                {t.calc.savings_value}
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                {t.calc.savings_note}
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-card border border-border rounded-sm p-8 md:p-12">
              <div className="space-y-10">
                <Field
                  testid="slider-amount"
                  label={t.calc.amount}
                  value={formatCZK(amount)}
                >
                  <Slider
                    value={[amount]}
                    min={500000}
                    max={20000000}
                    step={50000}
                    onValueChange={(v) => setAmount(v[0])}
                    data-testid="input-amount"
                  />
                  <div className="flex justify-between mt-2 text-xs text-muted-foreground font-mono">
                    <span>500 000 Kč</span>
                    <span>20 000 000 Kč</span>
                  </div>
                </Field>

                <Field
                  testid="slider-rate"
                  label={t.calc.rate}
                  value={`${rate.toFixed(2).replace(".", ",")} % p.a.`}
                >
                  <Slider
                    value={[rate]}
                    min={2}
                    max={9}
                    step={0.01}
                    onValueChange={(v) => setRate(v[0])}
                    data-testid="input-rate"
                  />
                  <div className="flex justify-between mt-2 text-xs text-muted-foreground font-mono">
                    <span>2,00 %</span>
                    <span>9,00 %</span>
                  </div>
                </Field>

                <Field
                  testid="slider-years"
                  label={t.calc.years}
                  value={t.calc.years_unit(years)}
                >
                  <Slider
                    value={[years]}
                    min={5}
                    max={30}
                    step={1}
                    onValueChange={(v) => setYears(v[0])}
                    data-testid="input-years"
                  />
                  <div className="flex justify-between mt-2 text-xs text-muted-foreground font-mono">
                    <span>{t.calc.years_unit(5)}</span>
                    <span>{t.calc.years_unit(30)}</span>
                  </div>
                </Field>
              </div>

              <div className="mt-12 pt-8 border-t border-border grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4">
                <Result
                  testid="result-monthly"
                  label={t.calc.monthly}
                  primary
                >
                  {formatCZK(monthly)}
                </Result>
                <Result testid="result-total" label={t.calc.total}>
                  {formatNumCZ(total)} Kč
                </Result>
                <Result testid="result-interest" label={t.calc.interest}>
                  {formatNumCZ(interest)} Kč
                </Result>
              </div>

              <a
                href="#contact"
                data-testid="calculator-cta-contact"
                className="mt-10 w-full inline-flex justify-center items-center px-6 py-4 bg-primary text-primary-foreground rounded-sm text-sm font-medium hover:bg-foreground transition-colors"
              >
                {t.calc.cta}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, value, children, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-baseline justify-between mb-5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="font-heading text-xl md:text-2xl font-semibold text-foreground tabular-nums">
        {value}
      </span>
    </div>
    {children}
  </div>
);

const Result = ({ label, children, primary, testid }) => (
  <div data-testid={testid}>
    <div className="overline text-muted-foreground">{label}</div>
    <div
      className={`mt-2 font-heading tabular-nums ${
        primary
          ? "text-3xl md:text-4xl font-semibold text-primary"
          : "text-xl md:text-2xl font-medium text-foreground"
      }`}
    >
      {children}
    </div>
  </div>
);

export default Calculator;
